import React from 'react';

interface GaugeChartProps {
  id?: string;
  percent?: number;
  arcWidth?: number;
  colors?: string[];
  hideText?: boolean;
  textColor?: string;
  fontSize?: string;
  formatTextValue?: ((value: number) => string) | null;
  arcPadding?: number;
  cornerRadius?: number;
  animate?: boolean;
  animateDuration?: number;
  needleColor?: string;
  needleBaseColor?: string;
  centerText?: string;
  centerTextColor?: string;
  centerTextSize?: string;
  style?: React.CSSProperties;
}

const GaugeChart: React.FC<GaugeChartProps> = ({
  id = 'gauge-chart',
  percent = 0.5,
  arcWidth = 0.2,
  colors = ['#FF5F6D', '#FFC371', '#5BE12C'],
  hideText = false,
  textColor = '#000',
  fontSize = '1rem',
  formatTextValue = null,
  arcPadding = 0.05,
  cornerRadius = 3,
  animate = true,
  animateDuration = 1000,
  needleColor = '#464A4F',
  needleBaseColor = '#464A4F',
  centerText = '',
  centerTextColor = '#000',
  centerTextSize = '2rem',
  style = {}
}) => {
  const [animatedPercent, setAnimatedPercent] = React.useState<number>(animate ? 0 : percent);
  const svgRef = React.useRef<SVGSVGElement>(null);

  React.useEffect(() => {
    if (animate) {
      const duration = animateDuration;
      const startTime = Date.now();
      const startPercent = animatedPercent;

      const animateValue = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Easing function
        const easeOutCubic = (progress: number): number => 1 - Math.pow(1 - progress, 3);
        const currentPercent = startPercent + (percent - startPercent) * easeOutCubic(progress);
        
        setAnimatedPercent(currentPercent);

        if (progress < 1) {
          requestAnimationFrame(animateValue);
        }
      };

      requestAnimationFrame(animateValue);
    } else {
      setAnimatedPercent(percent);
    }
  }, [percent, animate, animateDuration, animatedPercent]);

  const width = 500;
  const height = 300;
  const centerX = width / 2;
  const centerY = height - 50;
  const radius = Math.min(width, height * 2) / 2 - 20;

  // Calculate arc paths
  const startAngle = -Math.PI;
  const endAngle = 0;
  const totalAngle = endAngle - startAngle;

  const createArcPath = (start: number, end: number, innerRadius: number, outerRadius: number): string => {
    const startAngleRad = startAngle + start * totalAngle;
    const endAngleRad = startAngle + end * totalAngle;

    const x1 = centerX + outerRadius * Math.cos(startAngleRad);
    const y1 = centerY + outerRadius * Math.sin(startAngleRad);
    const x2 = centerX + outerRadius * Math.cos(endAngleRad);
    const y2 = centerY + outerRadius * Math.sin(endAngleRad);
    const x3 = centerX + innerRadius * Math.cos(endAngleRad);
    const y3 = centerY + innerRadius * Math.sin(endAngleRad);
    const x4 = centerX + innerRadius * Math.cos(startAngleRad);
    const y4 = centerY + innerRadius * Math.sin(startAngleRad);

    const largeArc = (endAngleRad - startAngleRad) > Math.PI ? 1 : 0;

    return `
      M ${x1} ${y1}
      A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${x2} ${y2}
      L ${x3} ${y3}
      A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${x4} ${y4}
      Z
    `;
  };

  const outerRadius = radius;
  const innerRadius = radius * (1 - arcWidth);

  // Create color segments
  const segments = colors.length;
  const segmentSize = 1 / segments;
  const padding = arcPadding / segments;

  // Calculate needle angle
  const needleAngle = startAngle + animatedPercent * totalAngle;
  const needleLength = radius * 0.7;
  const needleX = centerX + needleLength * Math.cos(needleAngle);
  const needleY = centerY + needleLength * Math.sin(needleAngle);

  // Format text value
  const displayValue = formatTextValue 
    ? formatTextValue(animatedPercent) 
    : `${Math.round(animatedPercent * 100)}%`;

  return (
    <div style={{ width: '100%', maxWidth: '500px', ...style }}>
      <svg
        ref={svgRef}
        id={id}
        viewBox={`0 0 ${width} ${height}`}
        style={{ width: '100%', height: 'auto' }}
      >
        {/* Background arcs */}
        {colors.map((color, index) => {
          const start = index * segmentSize + (index > 0 ? padding : 0);
          const end = (index + 1) * segmentSize - padding;
          
          return (
            <path
              key={`segment-${index}`}
              d={createArcPath(start, end, innerRadius, outerRadius)}
              fill={color}
              opacity={animatedPercent >= start ? 1 : 0.3}
            />
          );
        })}

        {/* Needle */}
        <g>
          {/* Needle line */}
          <line
            x1={centerX}
            y1={centerY}
            x2={needleX}
            y2={needleY}
            stroke={needleColor}
            strokeWidth="5"
            strokeLinecap="round"
          />
          {/* Needle base */}
          <circle
            cx={centerX}
            cy={centerY}
            r="8"
            fill={needleBaseColor}
          />
          <circle
            cx={centerX}
            cy={centerY}
            r="4"
            fill="#fff"
          />
        </g>

        {/* Text */}
        {!hideText && (
          <text
            x={centerX}
            y={centerY + 40}
            textAnchor="middle"
            fill={textColor}
            fontSize={fontSize}
            fontWeight="600"
          >
            {displayValue}
          </text>
        )}

        {/* Center Text (Bold) */}
        {centerText && (
          <text
            x={centerX}
            y={centerY - 20}
            textAnchor="middle"
            fill={centerTextColor}
            fontSize={centerTextSize}
            fontWeight="700"
          >
            {centerText}
          </text>
        )}
      </svg>
    </div>
  );
};

export default GaugeChart;